import { useFormContext } from "react-hook-form"
import { useFragment } from "react-relay"
import { t } from "i18next"
import { PotentiallyUnsafeInnerHtml } from "@/components/potentially-unsafe-inner-html"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { DetailCheckbox } from "@/components/ui/checkbox"
import { FormField } from "@/components/ui/form"
import { useMedications } from "@/modules/patients/detail/medication/hooks/useMedications"
import { MedicationFragmentInline$data } from "@/modules/patients/detail/medication/types/medication-fragment-type"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import {
  PatientAllergiesOutputFragment$data,
  PatientAllergiesOutputFragment$key,
} from "@/modules/patients/services/__generated__/PatientAllergiesOutputFragment.graphql"
import {
  PatientConditionInformationOutputFragment$data,
  PatientConditionInformationOutputFragment$key,
} from "@/modules/patients/services/__generated__/PatientConditionInformationOutputFragment.graphql"
import { PatientAllergiesOutputFragment } from "@/modules/patients/services/PatientAllergiesOutputFragment"
import { PatientConditionInformationOutputFragment } from "@/modules/patients/services/PatientConditionInformationOutputFragment"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import { ArrElement } from "@/types/ArrElement"

type ConditionIcdCode = ArrElement<PatientConditionInformationOutputFragment$data["conditions"]>["icdDiagnosis"]
type AllergyIcdCode = ArrElement<PatientAllergiesOutputFragment$data["allergies"]>["icdDiagnosis"]

export const MedicalHistory = () => {
  const patientId = usePatientParam()
  const countryCode = useCountryParam()
  const appointmentId = useAppointmentParam()
  const [patientData] = usePatientByIdQuery(patientId, countryCode, appointmentId)
  const { activeMedications, inactiveMedications } = useMedications()

  const medicationList = [...activeMedications, ...inactiveMedications]

  const { conditionInformation: conditionInformationRef, allergiesInformation: allergiesInformationRef } =
    patientData.patientById!

  const { conditions: conditionList, status: conditionStatus } =
    useFragment<PatientConditionInformationOutputFragment$key>(
      PatientConditionInformationOutputFragment,
      conditionInformationRef,
    )

  const { allergies: allergyList, status: allergyStatus } = useFragment<PatientAllergiesOutputFragment$key>(
    PatientAllergiesOutputFragment,
    allergiesInformationRef,
  )

  const { control } = useFormContext()

  return (
    <div className="flex flex-col gap-4 pt-8">
      <div className="flex flex-col">
        <label className="text-on-surface text-base font-medium">
          {t("patient.therapyPrescription.medicalHistory")}
        </label>
        <span className="text-on-surface-subdued text-sm font-normal">
          {t("patient.therapyPrescription.medicalHistoryDescription")}
        </span>
      </div>
      <Accordion type="single" collapsible={true}>
        <AccordionItem value="Conditions" disabled={conditionStatus !== "SOME"}>
          <FormField
            control={control}
            name="conditions"
            render={({ field: { onChange, value } }) => (
              <>
                <AccordionTrigger>
                  {t("patient.therapyPrescription.conditions")}
                  {!!value && ` (${value.length})`}
                </AccordionTrigger>
                <AccordionContent>
                  <CheckboxSelection
                    list={conditionList.map((item) => item.icdDiagnosis)}
                    onChange={onChange}
                    value={value ?? []}
                  />
                </AccordionContent>
              </>
            )}
          />
        </AccordionItem>

        <AccordionItem value="Allergies" disabled={allergyStatus !== "SOME"}>
          <FormField
            control={control}
            name="allergies"
            render={({ field: { onChange, value } }) => (
              <>
                <AccordionTrigger>
                  {t("patient.therapyPrescription.allergies")} {!!value && `(${value.length})`}
                </AccordionTrigger>
                <AccordionContent>
                  <CheckboxSelection
                    list={allergyList.map((item) => item.icdDiagnosis)}
                    onChange={onChange}
                    value={value ?? []}
                  />
                </AccordionContent>
              </>
            )}
          />
        </AccordionItem>
        <AccordionItem value="Medication" disabled={medicationList.length === 0}>
          <FormField
            control={control}
            name="medicationIds"
            render={({ field: { onChange, value } }) => (
              <>
                <AccordionTrigger>
                  {t("patient.therapyPrescription.medication")} {!!value && `(${value.length})`}
                </AccordionTrigger>
                <AccordionContent>
                  <CheckboxSelection list={medicationList} onChange={onChange} value={value ?? []} />
                </AccordionContent>
              </>
            )}
          />
        </AccordionItem>
      </Accordion>
    </div>
  )
}

type CheckboxItem = ConditionIcdCode | AllergyIcdCode | MedicationFragmentInline$data

interface CheckboxSelection {
  list: CheckboxItem[]
  value: string[]
  onChange: (item: string[]) => void
}

const CheckboxSelection = ({ list, value, onChange }: CheckboxSelection): React.JSX.Element => {
  const isSelectAll = value.length === list.length

  const handleSelectAll = () => {
    onChange([...list.map((item) => item.id)])
  }

  const handleDeselectAll = () => {
    onChange([])
  }

  const handleBulkToggle = () => {
    if (!isSelectAll) handleSelectAll()
    else handleDeselectAll()
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2">
        {list.map((item) => {
          const isChecked = value.find((v) => v === item.id)
          return (
            <DetailCheckbox
              data-testid={`${item.id}-checkbox`}
              key={item.id}
              className="pr-1.25"
              checked={!!isChecked}
              onCheckedChange={(checked) =>
                checked ? onChange([...value, item.id]) : onChange(value.filter((value) => value !== item.id))
              }>
              {"text" in item ? item.text : <PotentiallyUnsafeInnerHtml value={item.name} />}
            </DetailCheckbox>
          )
        })}
      </div>

      <Button variant="tertiary" onClick={handleBulkToggle}>
        {!isSelectAll ? t("common.button.selectAll") : t("common.button.deselectAll")}
      </Button>
    </>
  )
}
